import { PortalPositionRow, PortalPositionTableProps } from '@/components/portal-modules/PortalPositionTable';
import { GroupBy } from '@/components/trader-modules/Blotters/PositionGroupSwitch';
import { convertedQtyFormatterConfig, defaultColumn } from '@/config/config';
import { portalPositionColumns } from '@/helpers/portalPositionHelper';
import { useAppSelector } from '@/state/hooks';
import { selectCurrentAccount } from '@/state/reducers/authSlice';
import {
    ColumnDef,
    ExpandedState,
    filterFns,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';
import BigNumber from 'bignumber.js';
import { useEffect, useMemo, useState } from 'react';
import { useDateFormatter, useNumberFormatter } from 'react-aria';
import { useDebouncedValue } from './useDebouncedValue';
import { useDidUpdate } from './useDidUpdate';
import { fuzzyGlobalFilter } from './useOpenPositionTable';
import { useTrueDateFormatter } from './useTrueDateFormatter';

// Helper function to group rows by instrument name
const basicGrouping = (item: PortalPositionRow, groupedMap) => {
    const {
        instrumentName,
        type,
        amount,
        currentPrice,
        unrealizedProfitLoss,
        unrealizedProfitLossInBaseCurrency,
        unsettledProfitLoss,
        unsettledProfitLossInBaseCurrency,
        marketValueInBaseCurrency,
        totalProfitLoss,
        totalProfitLossInBaseCurrency,
        valueDate,
        subRows
    } = item;
    const formattedType = type === 'CFDs on Futures' ? 'Futures CFDs' : type || '';
    let tableInstrumentName = instrumentName;
    if (type === 'CFDs on Futures') {
        tableInstrumentName = `CFD/${instrumentName}`;
    }

    if (groupedMap.has(instrumentName)) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const group = groupedMap.get(instrumentName)!;

        group.currentPrice = currentPrice;
        group.amount = BigNumber(group.amount).plus(amount).toNumber();

        group.marketValueInBaseCurrency = BigNumber(group.marketValueInBaseCurrency || 0)
            .plus(marketValueInBaseCurrency || 0)
            .toNumber();

        group.totalProfitLoss = BigNumber(group.totalProfitLoss)
            .plus(totalProfitLoss || 0)
            .toNumber();

        group.totalProfitLossInBaseCurrency = BigNumber(group.totalProfitLossInBaseCurrency || 0)
            .plus(totalProfitLossInBaseCurrency || 0)
            .toNumber();

        group.unrealizedProfitLoss = BigNumber(group.unrealizedProfitLoss || 0)
            .plus(unrealizedProfitLoss || 0)
            .toNumber();
        group.unsettledProfitLoss = BigNumber(group.unsettledProfitLoss || 0)
            .plus(unsettledProfitLoss || 0)
            .toNumber();

        group.subRows.push({
            instrumentName: tableInstrumentName,
            type: formattedType,
            amount,
            currentPrice,
            unrealizedProfitLoss,
            unrealizedProfitLossInBaseCurrency,
            unsettledProfitLoss,
            unsettledProfitLossInBaseCurrency,
            marketValueInBaseCurrency,
            totalProfitLoss,
            totalProfitLossInBaseCurrency,
            valueDate
        });
    } else {
        groupedMap.set(tableInstrumentName, {
            instrumentName: tableInstrumentName,
            type: formattedType,
            amount,
            currentPrice,
            unrealizedProfitLoss,
            unrealizedProfitLossInBaseCurrency,
            unsettledProfitLoss,
            unsettledProfitLossInBaseCurrency,
            marketValueInBaseCurrency,
            totalProfitLoss,
            totalProfitLossInBaseCurrency,
            valueDate,
            subRows
        });
    }
};

// Function to group rows by Instrument Type
const groupRowsByInstrument = (rows: PortalPositionRow[], groupBy) => {
    const productGroupedMap = new Map<string, any>();
    rows.forEach((item) => {
        const {
            instrumentName,
            type,
            amount,
            currentPrice,
            unrealizedProfitLoss,
            unrealizedProfitLossInBaseCurrency,
            unsettledProfitLoss,
            unsettledProfitLossInBaseCurrency,
            marketValueInBaseCurrency,
            totalProfitLoss,
            totalProfitLossInBaseCurrency,
            valueDate,
            subRows
        } = item;

        const formattedType = type === 'CFDs on Futures' ? 'Futures CFDs' : type || '';
        let tableInstrumentName = instrumentName;
        if (type === 'CFDs on Futures') {
            tableInstrumentName = `CFD/${instrumentName}`;
        }

        if (productGroupedMap.has(formattedType)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const productGroup = productGroupedMap.get(formattedType)!;
            productGroup.marketValueInBaseCurrency = BigNumber(productGroup.marketValueInBaseCurrency || 0)
                .plus(marketValueInBaseCurrency || 0)
                .toNumber();
            productGroup.unrealizedProfitLossInBaseCurrency = BigNumber(
                productGroup.unrealizedProfitLossInBaseCurrency || 0
            )
                .plus(unrealizedProfitLossInBaseCurrency || 0)
                .toNumber();

            productGroup.totalProfitLoss = BigNumber(productGroup.totalProfitLoss || 0)
                .plus(totalProfitLoss || 0)
                .toNumber();

            productGroup.totalProfitLossInBaseCurrency = BigNumber(productGroup.totalProfitLossInBaseCurrency || 0)
                .plus(totalProfitLossInBaseCurrency || 0)
                .toNumber();

            basicGrouping(item, productGroup.subRows);
        } else {
            productGroupedMap.set(formattedType, {
                instrumentName: tableInstrumentName,
                type: formattedType,
                amount,
                currentPrice,
                unrealizedProfitLoss,
                unrealizedProfitLossInBaseCurrency,
                unsettledProfitLoss,
                unsettledProfitLossInBaseCurrency,
                totalProfitLoss,
                totalProfitLossInBaseCurrency,
                marketValueInBaseCurrency,
                valueDate,
                subRows: new Map<string, any>().set(tableInstrumentName, {
                    instrumentName: tableInstrumentName,
                    type: formattedType,
                    amount,
                    currentPrice,
                    unrealizedProfitLoss,
                    unrealizedProfitLossInBaseCurrency,
                    unsettledProfitLoss,
                    unsettledProfitLossInBaseCurrency,
                    marketValueInBaseCurrency,
                    totalProfitLoss,
                    totalProfitLossInBaseCurrency,
                    valueDate,
                    subRows
                })
            });
        }
    });
    const returnValues = Array.from(productGroupedMap.keys()).map((key) => {
        const productGroup = productGroupedMap.get(key);
        return {
            instrumentName: key,
            type: key,
            amount: key,
            currentPrice: key,
            unrealizedProfitLoss: key,
            unrealizedProfitLossInBaseCurrency: productGroup.unrealizedProfitLossInBaseCurrency,
            unsettledProfitLoss: key,
            unsettledProfitLossInBaseCurrency: productGroup.unsettledProfitLossInBaseCurrency,
            marketValueInBaseCurrency: productGroup.marketValueInBaseCurrency,
            totalProfitLoss: productGroup.totalProfitLoss,
            totalProfitLossInBaseCurrency: productGroup.totalProfitLossInBaseCurrency,
            valueDate: key,
            subRows: Array.from(productGroup.subRows.values())
        };
    });

    return returnValues;
};

interface UsePortalPositionTableProps extends PortalPositionTableProps {
    groupBy: GroupBy;
    setInstrumentInfoContext: React.Dispatch<React.SetStateAction<any>>;
    handleOpenContextMenu: (row: any, x: number, y: number) => void;
}

const usePortalPositionTable = (props: UsePortalPositionTableProps) => {
    const { summary, baseCurrency, groupBy, setInstrumentInfoContext, handleOpenContextMenu } = props;

    const currentAccount = useAppSelector(selectCurrentAccount);
    const formatQty = useNumberFormatter(convertedQtyFormatterConfig);
    const formatDate = useTrueDateFormatter();

    const dateFormatter = useDateFormatter({
        timeZone: 'UTC'
    });

    const [expandedRows, setExpandedRows] = useState<ExpandedState>({});
    const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});
    const [sorting, setSorting] = useState<SortingState>([]);
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [hydrated, setHydrated] = useState<boolean>(false);

    const columns: ColumnDef<PortalPositionRow>[] = useMemo(() => {
        return portalPositionColumns({
            groupBy,
            formatQty,
            formatDate,
            baseCurrency,
            setInstrumentInfoContext,
            handleOpenContextMenu
        });
    }, [groupBy, formatQty, setInstrumentInfoContext, handleOpenContextMenu]);

    const formattedPortalPositions: PortalPositionRow[] = useMemo(() => {
        if (summary) {
            const fxPositions = summary.profitLossSummary?.fx?.data || [];
            const indexCfdPositions = summary.profitLossSummary?.indexCfd?.data || [];
            const cryptoCfdPositions = summary.profitLossSummary?.cryptoCfd?.data || [];
            const futureCfdPositions = summary.profitLossSummary?.futureCfd?.data || [];

            const aggregatedPositions: any = [
                ...fxPositions.map((pos) => ({
                    ...pos,
                    subRows: pos.subRows?.filter((row) => row.instrumentName !== 'Settlement Date') || []
                })),
                ...indexCfdPositions.map((pos) => ({
                    ...pos,
                    subRows: pos.subRows?.filter((row) => row.instrumentName !== 'Settlement Date') || []
                })),
                ...cryptoCfdPositions.map((pos) => ({
                    ...pos,
                    subRows: pos.subRows?.filter((row) => row.instrumentName !== 'Settlement Date') || []
                })),
                ...futureCfdPositions.map((pos) => ({
                    ...pos,
                    subRows: pos.subRows?.filter((row) => row.instrumentName !== 'Settlement Date') || []
                }))
            ];

            aggregatedPositions.sort((a, b) => {
                if (a.instrumentName < b.instrumentName) return -1;
                if (a.instrumentName > b.instrumentName) return 1;
                return 0;
            });

            if (groupBy === 'None') {
                return aggregatedPositions;
            } else if (groupBy === 'Products') {
                return groupRowsByInstrument(aggregatedPositions, groupBy);
            }
        }
        return [];
    }, [summary, groupBy]);

    const tableInstance = useReactTable({
        data: formattedPortalPositions,
        columns,
        state: { expanded: expandedRows, globalFilter, columnSizing, sorting },
        defaultColumn: { ...defaultColumn, filterFn: filterFns.includesString },
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        globalFilterFn: (row, columnId, filterValue) => {
            return fuzzyGlobalFilter(row, columnId, filterValue, dateFormatter);
        },
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        onExpandedChange: setExpandedRows,
        onColumnSizingChange: setColumnSizing,
        onSortingChange: setSorting,
        getExpandedRowModel: getExpandedRowModel(),
        getSubRows: (row: any) => row.subRows,
        enableRowSelection: true
    });

    const [debouncedTableInstance] = useDebouncedValue(tableInstance, 500);

    useEffect(() => {
        if (summary && !hydrated) {
            setHydrated(true);
        }
    }, [summary]);

    useDidUpdate(() => {
        if (groupBy === 'None') {
            setExpandedRows({});
        } else if (groupBy === 'Products') {
            const newExpandedRows = {};
            formattedPortalPositions.forEach((row, i) => {
                newExpandedRows[i] = true;
            });
            setExpandedRows(newExpandedRows);
        }
    }, [groupBy, hydrated, currentAccount]);

    return { debouncedTableInstance, columnSizing, sorting, formattedPortalPositions, expandedRows, globalFilter };
};

export default usePortalPositionTable;
